import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatErr } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Check, ArrowRight } from "@phosphor-icons/react";
import SEOHead from "@/lib/seo/SEOHead";
import { toast } from "sonner";

const PRIMARY = "#2D5BFF";
const ACCENT  = "#65CE95";
const DARK    = "#0F1311";
const ALT     = "#F5F7F4";

const Logo = ({ className = "", height = 40, tagline = false }) => (
  <div className={className} style={{ display: "inline-block" }}>
    <img
      src="/ranktrust-logo.png?v=5"
      alt="RankTrust.io"
      style={{ height, width: "auto", display: "block" }}
    />
    {tagline && (
      <div
        style={{
          fontSize: 10,
          letterSpacing: "1.5px",
          color: "#6B7280",
          fontWeight: 600,
          textTransform: "uppercase",
          marginTop: 4,
          whiteSpace: "nowrap",
        }}
      >
        The Business Growth Intelligence Platform
      </div>
    )}
  </div>
);

const featuresB2C = {
  launch:   ["1 location", "15 keywords", "AI audit + posts (4/mo)", "AI review reply drafts", "Rank tracker"],
  growth:   ["1 location", "50 keywords", "Geo-grid (5×5)", "Competitor spy", "Q&A automation", "Monthly PDF report"],
  dominate: ["1 location", "Unlimited keywords", "Priority AI agent", "Top-3 progress guarantee", "Industry playbook", "Weekly human review"],
  multi:    ["10 locations", "Roll-up reporting", "Dedicated success manager", "Everything in Dominate"],
};
const featuresAgency = {
  agency_starter:    ["10 client seats", "Whitelabel branding", "Branded PDF reports", "IntentBrain webhook setup", "Growth + Trust reporting"],
  agency_growth:     ["30 client seats", "Custom domain", "API access", "Priority support", "Prospecting → IntentBrain push", "Custom IntentBrain workflow routing"],
  agency_scale:      ["100 client seats", "Dedicated onboarding", "Co-branded sales assets", "Multi-tenant IntentBrain routing", "Advanced Growth Stack reporting"],
  agency_enterprise: ["Unlimited seats", "SSO / SLA", "Private Slack support", "Dedicated IntentBrain instance", "Custom growth playbooks"],
};

export default function Pricing() {
  const { user } = useAuth();
  const [plans, setPlans] = useState([]);
  const [busy, setBusy] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/plans");
        setPlans(data.plans);
      } catch {}
    })();
  }, []);

  const checkout = async (plan_id) => {
    if (!user) {
      window.location.href = "/register";
      return;
    }
    setBusy(plan_id);
    try {
      const { data } = await api.post("/billing/checkout", {
        plan_id,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) { toast.error(formatErr(e)); }
    setBusy("");
  };

  const b2c = plans.filter((p) => p.tier === "b2c");
  const agency = plans.filter((p) => p.tier === "agency");

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: "Manrope, system-ui, sans-serif", color: DARK }}>
      <SEOHead path="/pricing" />
      {/* Nav — dark like landing */}
      <header className="sticky top-0 z-40 backdrop-blur-md" style={{ background: `${DARK}E6`, borderBottom: `1px solid #1F2622` }}>
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-[72px] flex items-center justify-between">
          <Link to="/" data-testid="pricing-brand"><Logo height={40} tagline /></Link>
          <div className="flex items-center gap-2">
            {user ? (
              <Link to="/app" className="text-sm font-bold px-5 py-2.5 rounded-full" style={{ background: PRIMARY, color: "#fff" }}>Open app</Link>
            ) : (
              <>
                <Link to="/login" className="text-sm font-semibold text-zinc-400 hover:text-white px-3 py-2 transition-colors">Log in</Link>
                <Link to="/register" className="text-sm font-bold px-5 py-2.5 rounded-full transition-all hover:-translate-y-0.5" style={{ background: PRIMARY, color: "#fff" }} data-testid="pricing-register">Start Free Trial</Link>
              </>
            )}
          </div>
        </div>
      </header>

      {/* HERO — dark */}
      <section className="relative overflow-hidden" style={{ background: DARK, color: "#fff" }}>
        <div className="absolute inset-0 opacity-[0.12] pointer-events-none" style={{
          backgroundImage: `linear-gradient(#1F2622 1px, transparent 1px), linear-gradient(90deg, #1F2622 1px, transparent 1px)`,
          backgroundSize: "60px 60px",
        }}></div>
        <div className="absolute -top-32 -right-32 w-[600px] h-[600px] rounded-full opacity-20 pointer-events-none blur-3xl" style={{ background: PRIMARY }}></div>
        <div className="absolute -bottom-32 -left-32 w-[500px] h-[500px] rounded-full opacity-20 pointer-events-none blur-3xl" style={{ background: ACCENT }}></div>

        <div className="relative max-w-5xl mx-auto px-6 lg:px-10 py-10 lg:py-28 text-center">
          <div className="text-xs font-bold tracking-[0.2em] uppercase mb-3 lg:mb-4" style={{ color: ACCENT }}>Google Business Profile Ranking Intelligence</div>
          <h1 className="font-black tracking-tight leading-[1.05]" style={{ fontSize: "clamp(1.875rem, 6vw, 5rem)" }}>
            Simple. <span style={{ color: PRIMARY }}>Outcome-priced.</span>
          </h1>
          <p className="mt-4 lg:mt-6 text-zinc-400 text-base lg:text-lg max-w-2xl mx-auto leading-relaxed">
            One new customer pays this back 5×. Choose the tier that fits your competition.
          </p>
          <a
            href="#plans"
            onClick={(e) => {
              e.preventDefault();
              document.getElementById("plans")?.scrollIntoView({ behavior: "smooth", block: "start" });
            }}
            className="mt-6 lg:mt-8 inline-flex items-center gap-2 px-6 py-3 rounded-full font-bold text-sm transition-all hover:-translate-y-0.5"
            style={{ background: PRIMARY, color: "#fff" }}
            data-testid="pricing-see-plans"
          >
            See plans <ArrowRight size={14} weight="bold" />
          </a>
        </div>
      </section>

      {/* B2C plans — light */}
      <section id="plans" className="py-14 lg:py-24 scroll-mt-20" style={{ background: ALT }}>
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="mb-8 lg:mb-12 text-center">
            <div className="text-xs font-bold tracking-[0.2em] uppercase mb-3 lg:mb-4" style={{ color: PRIMARY }}>For Local Businesses</div>
            <h2 className="text-2xl lg:text-4xl font-black tracking-tight">Direct plans — rank your own GBP</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {b2c.map((p) => (
              <PlanCard
                key={p.id}
                plan={p}
                features={featuresB2C[p.id]}
                onClick={() => checkout(p.id)}
                busy={busy === p.id}
                featured={p.id === "growth"}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Agency plans — dark */}
      <section className="py-20 lg:py-24" style={{ background: DARK, color: "#fff" }}>
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="mb-12 text-center">
            <div className="text-xs font-bold tracking-[0.2em] uppercase mb-4" style={{ color: ACCENT }}>For Agencies · RankTrust.io + IntentBrain.ai</div>
            <h2 className="text-3xl lg:text-4xl font-black tracking-tight">The Agency Growth Stack — <span style={{ color: ACCENT }}>10× margin</span></h2>
            <p className="mt-4 text-zinc-400 text-sm lg:text-base max-w-2xl mx-auto">
              Authority Engine (RankTrust) closes & retains clients. AI Growth Engine (IntentBrain) finds and routes prospects. One stack, fully whitelabeled.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {agency.map((p) => (
              <PlanCard
                key={p.id}
                plan={p}
                features={featuresAgency[p.id]}
                onClick={() => checkout(p.id)}
                busy={busy === p.id}
                dark
                featured={p.id === "agency_growth"}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-14" style={{ background: DARK, color: "#fff", borderTop: `1px solid #1F2622` }}>
        <div className="max-w-6xl mx-auto px-6 lg:px-10">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10 pb-10">
            <div>
              <Logo height={36} />
              <div className="text-xs text-zinc-400 mt-2 tracking-[0.15em] uppercase font-bold">The Business Growth Intelligence Platform</div>
            </div>
            <div>
              <div className="text-[10px] font-bold tracking-[0.2em] uppercase text-zinc-500 mb-4">Platform</div>
              <ul className="space-y-2.5 text-sm text-zinc-300">
                <li><Link to="/" className="hover:text-white transition-colors">Home</Link></li>
                <li><Link to="/check" className="hover:text-white transition-colors">Free Business Growth Assessment</Link></li>
                <li><Link to="/login" className="hover:text-white transition-colors">Log in</Link></li>
              </ul>
            </div>
            <div>
              <div className="text-[10px] font-bold tracking-[0.2em] uppercase text-zinc-500 mb-4">Legal</div>
              <ul className="space-y-2.5 text-sm text-zinc-300">
                <li><Link to="/terms" className="hover:text-white transition-colors">Terms</Link></li>
                <li><Link to="/privacy" className="hover:text-white transition-colors">Privacy</Link></li>
                <li><a href="mailto:support@ranktrust.io" className="hover:text-white transition-colors">Contact</a></li>
              </ul>
            </div>
          </div>
          <div className="pt-8 border-t border-zinc-800 text-xs text-zinc-500 space-y-2">
            <div data-testid="footer-legal-entity">
              RankTrust is a division of Card Service Warehouse, Inc., operating under the same legal entity and EIN.
            </div>
            <div>© {new Date().getFullYear()} RankTrust · The Business Growth Intelligence Platform</div>
          </div>
        </div>
      </footer>
    </div>
  );
}

function PlanCard({ plan, features, onClick, busy, dark, featured }) {
  // featured = highlighted card with primary blue accent
  // dark = inside the dark section
  const baseBg = featured ? PRIMARY : dark ? "#171C19" : "#fff";
  const baseText = featured ? "#fff" : dark ? "#fff" : DARK;
  const subText = featured ? "rgba(255,255,255,0.7)" : dark ? "#a1a1aa" : "#71717a";
  const border = featured ? PRIMARY : dark ? "#1F2622" : "#e5e7eb";
  const checkColor = featured ? "#fff" : ACCENT;

  return (
    <div
      className="rounded-2xl p-7 flex flex-col transition-all hover:-translate-y-1 hover:shadow-xl"
      style={{ background: baseBg, color: baseText, border: `1px solid ${border}` }}
      data-testid={`plan-${plan.id}`}
    >
      {featured && (
        <div className="inline-flex self-start mb-4 px-2.5 py-1 rounded-full text-[10px] font-bold tracking-widest uppercase" style={{ background: "rgba(255,255,255,0.18)", color: "#fff" }}>
          Most popular
        </div>
      )}
      <h3 className="text-xl font-bold">{plan.name}</h3>
      <div className="mt-3 flex items-baseline gap-1">
        <span className="text-5xl font-black tabular-nums">${Math.round(plan.price)}</span>
        <span style={{ color: subText }}>/mo</span>
      </div>
      <ul className="mt-6 space-y-2.5 text-sm flex-1">
        {(features || []).map((f, i) => (
          <li key={i} className="flex items-start gap-2">
            <Check size={14} weight="bold" style={{ color: checkColor }} className="mt-0.5 shrink-0" />
            <span style={{ color: featured ? "rgba(255,255,255,0.92)" : baseText }}>{f}</span>
          </li>
        ))}
      </ul>
      <button
        className="mt-7 w-full font-bold text-sm py-3.5 rounded-full transition-all hover:-translate-y-0.5 flex items-center justify-center gap-1.5"
        style={{
          background: featured ? "#fff" : dark ? "#fff" : DARK,
          color: featured ? PRIMARY : dark ? DARK : "#fff",
        }}
        onClick={onClick}
        disabled={busy}
        data-testid={`plan-cta-${plan.id}`}
      >
        {busy ? (
          <span className="inline-block w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin"></span>
        ) : (
          <>Choose {plan.name} <ArrowRight size={14} weight="bold" /></>
        )}
      </button>
    </div>
  );
}
